import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Camera } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/components/AuthProvider";
import { IKO } from "@/design-system";
import MomentIllustration from "./MomentIllustration";

/**
 * Vania Msg 227 (Feb 2026) — seven-screen intake. Warm, indirect, not just
 * about nights. Skippable except the name. Never says "night out".
 *
 *   1  about you: name (required), @handle, photo, one-line bio (80)
 *   2  your perfect moments: 12 picture cards, multi
 *   3  the best times out leave you…: 6 feelings, multi
 *   4  who's usually with you?: 6 options, multi
 *   5  where do you usually head?: neighbourhood chips + anywhere
 *   6  a good outing is worth…: 4 budget options, single
 *   7  a day/night you still talk about: optional 2-line free text
 */
const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api`, withCredentials: true });
const YELLOW = "#FFD60A";
const META = "var(--ww-text-muted)";
const LINE = "var(--ww-line)";
const CARD = "var(--ww-surface)";
const TOTAL = 8;

const GENDERS = [
  { key: "woman",             label: "woman" },
  { key: "man",               label: "man" },
  { key: "non_binary",        label: "non-binary" },
  { key: "prefer_not_to_say", label: "prefer not to say" },
];

function absUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${API}${u}`;
}
function bearer() {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}
function slugFromName(n) {
  return (n || "").toLowerCase().replace(/[^a-z0-9_]+/g, "").slice(0, 30);
}

// Msg 232 · 24 moment cards, 6 per iko. Render order matters — sherehe · polite ·
// michezo · kazi so members see a spread as they scroll.
const MOMENTS = [
  // sherehe
  { key: "saturday_night_dance_floor", label: "saturday night on the dance floor", iko: "sherehe" },
  { key: "late_night_gig",             label: "a late night gig",             iko: "sherehe" },
  { key: "amapiano_day_party",         label: "an amapiano day party",        iko: "sherehe" },
  { key: "karaoke_night_crew",         label: "karaoke night with the crew",  iko: "sherehe" },
  { key: "friday_rooftop_party",       label: "a friday rooftop party",       iko: "sherehe" },
  { key: "festival_weekend",           label: "a festival weekend",           iko: "sherehe" },
  // polite
  { key: "tuesday_jazz_night",         label: "a tuesday jazz night",         iko: "polite" },
  { key: "friday_drinks_after_work",   label: "friday drinks after work",     iko: "polite" },
  { key: "saturday_afternoon_gallery", label: "a saturday afternoon gallery", iko: "polite" },
  { key: "sunday_brunch_runs_long",    label: "a sunday brunch that runs long", iko: "polite" },
  { key: "rooftop_at_sunset",          label: "a rooftop at sunset",          iko: "polite" },
  { key: "market_day",                 label: "a market day",                 iko: "polite" },
  // michezo
  { key: "saturday_morning_hike",      label: "a saturday morning hike",      iko: "michezo" },
  { key: "sunday_afternoon_match",     label: "a sunday afternoon match",     iko: "michezo" },
  { key: "games_night_with_friends",   label: "games night with friends",     iko: "michezo" },
  { key: "morning_run_club",           label: "a morning run club",           iko: "michezo" },
  { key: "padel_after_work",           label: "padel after work",             iko: "michezo" },
  { key: "big_game_sports_bar",        label: "the big game at a sports bar", iko: "michezo" },
  // kazi
  { key: "weeknight_talk_or_workshop", label: "a weeknight talk or workshop", iko: "kazi" },
  { key: "founders_breakfast",         label: "a founders breakfast",         iko: "kazi" },
  { key: "lunchtime_networking",       label: "a lunchtime networking meetup", iko: "kazi" },
  { key: "weekend_masterclass",        label: "a weekend masterclass",        iko: "kazi" },
  { key: "pitch_night",                label: "a pitch night",                iko: "kazi" },
  { key: "creative_coworking_day",     label: "a creative coworking day",     iko: "kazi" },
];

const FEELINGS = [
  { key: "voice_gone",       label: "voice gone" },
  { key: "feet_sore",        label: "feet sore" },
  { key: "muscles_aching",   label: "muscles aching" },
  { key: "belly_full",       label: "belly full" },
  { key: "heart_full",       label: "heart full" },
  { key: "mind_full",        label: "mind full" },
  { key: "inspired_to_make", label: "inspired to make something" },
  { key: "with_new_skill",   label: "with a new skill" },
  { key: "new_numbers",      label: "new numbers in your phone" },
  { key: "closer_to_people", label: "closer to your people" },
  { key: "camera_roll_full", label: "camera roll full" },
  { key: "rested_recharged", label: "rested and recharged" },
  { key: "bank_balance",     label: "bank balance intact" },
  { key: "with_a_story",     label: "with a story" },
];

const COMPANY = [
  { key: "solo",             label: "just me" },
  { key: "with_my_person",   label: "my person" },
  { key: "my_friends",       label: "my friends" },
  { key: "the_squad",        label: "the whole squad" },
  { key: "family_and_kids",  label: "family and kids" },
  { key: "it_changes",       label: "it changes every time" },
];

const AREA_GROUPS = [
  // Msg 234 §6 (Vania 30 Sep evening) · compass prefix on every side name.
  ["northwest · westlands side", ["Westlands", "Parklands", "Spring Valley", "Loresho", "Riverside", "Lavington"]],
  ["west · kilimani side",       ["Kilimani", "Kileleshwa", "Hurlingham", "Upper Hill", "Ngong Road"]],
  ["southwest · karen side",     ["Karen", "Lang'ata", "Rongai", "Ngong"]],
  ["north · gigiri side",        ["Gigiri", "Runda", "Muthaiga", "Rosslyn", "Ruaka", "Kiambu Road"]],
  ["northeast · thika road side",["Kasarani", "Roysambu", "Garden City", "Kahawa", "Ruiru", "Juja"]],
  ["city and south",             ["CBD", "Ngara", "South B", "South C", "Industrial Area", "Syokimau", "Kitengela"]],
  ["east · eastleigh side",      ["Eastleigh", "Buruburu", "Donholm", "Umoja", "Embakasi"]],
];
// Msg 232 · side keys used to mark a whole-side pick (the side name itself
// gets stored alongside its neighbourhoods so the ranker can weight it).
const SIDE_KEYS = AREA_GROUPS.map(([n]) => n);

const BUDGETS = [
  { key: "free",       label: "the free ones are the best" },
  { key: "under_1000", label: "under KES 1,000" },
  { key: "1000_3000",  label: "KES 1,000 to 3,000" },
  { key: "3000_plus",  label: "if it's good, whatever it costs" },
];


export default function JoinIntake() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const editMode = sp.get("edit") === "1";
  const { user, loading, refresh } = useAuth();

  const [step, setStep] = useState(1);
  // Profile (screen 1)
  const [displayName, setDisplayName] = useState("");
  const [handle, setHandle] = useState("");
  const [handleTouched, setHandleTouched] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [oneLiner, setOneLiner] = useState("");
  const [uploading, setUploading] = useState(false);
  const [nameError, setNameError] = useState("");
  const [handleError, setHandleError] = useState("");
  const fileRef = useRef(null);
  // Preferences (screens 2-7)
  const [moments, setMoments] = useState([]);
  const [feelings, setFeelings] = useState([]);
  const [company, setCompany] = useState([]);
  const [areasHome, setAreasHome] = useState([]);
  const [areasExplore, setAreasExplore] = useState([]);
  const [budget, setBudget] = useState("");
  const [memory, setMemory] = useState("");
  const [gender, setGender] = useState("");  // Wave 10D (Vania 1 Oct) · final step
  // Wave 12.18 (Vania 1 Oct) · required 18+ confirmation on step 1 for new
  // sign-ups. Existing members (age_confirmed_at already set) see no checkbox.
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [ageAlreadySet, setAgeAlreadySet] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && !user) nav("/admin/login?next=/app/welcome" + (editMode ? "?edit=1" : ""));
  }, [user, loading, nav, editMode]);

  // Pre-fill from current profile + preferences.
  useEffect(() => {
    if (!user) return;
    setDisplayName(user.display_name || user.name || "");
    setHandle((user.handle || "").replace(/^@+/, ""));
    setPhotoUrl(user.photo_url || user.picture || "");
    setOneLiner((user.bio || "").slice(0, 80));
    client.get("/me/preferences").then((r) => {
      const p = r.data.preferences || {};
      if (Array.isArray(p.moments))     setMoments(p.moments);
      if (Array.isArray(p.feelings))    setFeelings(p.feelings);
      if (Array.isArray(p.company))     setCompany(p.company);
      else if (typeof p.company === "string" && p.company) setCompany([p.company]);
      // Msg 228 · new split. Fall back to legacy `areas` for home.
      if (Array.isArray(p.areas_home))       setAreasHome(p.areas_home.map((a) => titleForArea(a)));
      else if (Array.isArray(p.areas))       setAreasHome(p.areas.map((a) => titleForArea(a)));
      if (Array.isArray(p.areas_explore))    setAreasExplore(p.areas_explore.map((a) => titleForArea(a)));
      if (typeof p.budget === "string")  setBudget(p.budget);
      if (typeof p.memory === "string")  setMemory(p.memory);
    }).catch(() => {});
    // Wave 10D · load existing gender if any, so re-entering the intake
    // doesn't blank it. Field lives on the user doc, not inside preferences.
    client.get("/me/gender/status").then((r) => {
      if (r.data?.gender) setGender(r.data.gender);
    }).catch(() => {});
    // Wave 12.18 · hide the 18+ check for existing members who already ticked
    // it. New sign-ups come in with age_confirmed_at = null → must tick.
    client.get("/me/age-status").then((r) => {
      if (r.data?.age_confirmed_at) {
        setAgeAlreadySet(true);
        setAgeConfirmed(true);
      }
    }).catch(() => {});
  }, [user]);

  const toggle = (arr, setArr, k) => setArr(arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]);
  const suggestedHandle = slugFromName(displayName);
  const effectiveHandle = handleTouched ? handle : (handle || suggestedHandle);

  const onPickPhoto = () => fileRef.current?.click();
  const onPhotoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("photo must be under 6MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await client.post("/me/photo", fd, {
        headers: { ...bearer(), "Content-Type": "multipart/form-data" },
      });
      setPhotoUrl(r.data.photo_url);
    } catch (err) {
      toast.error(err.response?.data?.detail?.error || "could not upload. try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Screen 1 validation before moving on.
  const validateStep1 = () => {
    setNameError(""); setHandleError("");
    const dn = displayName.trim();
    if (!dn) { setNameError("your name is required."); return false; }
    if (dn.length > 120) { setNameError("keep it under 120 characters."); return false; }
    const h = (handleTouched ? handle : suggestedHandle).trim().replace(/^@+/, "").toLowerCase();
    if (h && !/^[a-z0-9_]{2,30}$/.test(h)) {
      setHandleError("2-30 letters, numbers or underscores.");
      return false;
    }
    // Wave 12.18 · require 18+ tick for new sign-ups. Existing members pass.
    if (!ageAlreadySet && !ageConfirmed) {
      toast.error("please confirm you're 18 or older to continue.");
      return false;
    }
    return true;
  };

  const saveStep1 = async () => {
    const dn = displayName.trim();
    const h = (handleTouched ? handle : suggestedHandle).trim().replace(/^@+/, "").toLowerCase();
    try {
      await client.put("/auth/profile", {
        display_name: dn,
        handle: h,
        bio: oneLiner.trim().slice(0, 80),
      }, { headers: bearer() });
      if (refresh) refresh();
      return true;
    } catch (err) {
      const d = err.response?.data?.detail;
      if (typeof d === "string" && d.toLowerCase().includes("handle")) setHandleError(d);
      else toast.error(typeof d === "string" ? d : "could not save. try again.");
      return false;
    }
  };

  const submitPrefs = async (skip = false) => {
    setBusy(true);
    try {
      const payload = skip
        ? { skipped: true }
        : {
            moments,
            feelings,
            company,
            areas_home: areasHome.map((a) => a.toLowerCase()),
            areas_explore: areasExplore.map((a) => a.toLowerCase()),
            budget,
            memory: memory.trim().slice(0, 200),
          };
      await client.put("/me/preferences", payload);
      // Wave 10D (Vania 1 Oct) · persist gender on the user doc (always
      // mark asked=true, so the one-time sheet never pops for intake users).
      try {
        await client.put("/me/gender", { gender: gender || "", asked: true });
      } catch { /* non-fatal */ }
      // Wave 12.18 · stamp age_confirmed_at the first time, for new sign-ups.
      if (!ageAlreadySet && ageConfirmed) {
        try { await client.put("/me/age-confirm", { confirmed: true }); }
        catch { /* non-fatal */ }
      }
      if (refresh) refresh();
      // Msg 251 §5 · honour a deep link the guest came in on (e.g. /app/squad
      // or a specific squad URL). AuthCallback stashes it under
      // `watuwako:post_intake_next` when the intake gate is expected to run.
      let dest = "/app";
      try {
        const post = sessionStorage.getItem("watuwako:post_intake_next");
        if (post) {
          dest = post;
          sessionStorage.removeItem("watuwako:post_intake_next");
        }
      } catch {}
      nav(dest);
    } catch { toast.error("could not save. try again."); }
    finally { setBusy(false); }
  };

  const onNext = async () => {
    if (step === 1) {
      if (!validateStep1()) return;
      const ok = await saveStep1();
      if (!ok) return;
      setStep(2);
      return;
    }
    if (step < TOTAL) { setStep(step + 1); return; }
    submitPrefs(false);
  };

  const canSkip = step > 1; // name is required
  const initial = (displayName || handle || "?").slice(0, 1).toUpperCase();

  const stepDots = (
    <div className="flex items-center gap-2" data-testid="progress-dots">
      {Array.from({ length: TOTAL }).map((_, i) => (
        <span key={i} style={{ width: 10, height: 10, borderRadius: 999,
                               background: (i + 1) <= step ? YELLOW : LINE }} />
      ))}
    </div>
  );

  return (
    <div style={{ minHeight: "100dvh", background: "var(--ww-ground)", color: "var(--ww-text)",
                  display: "flex", flexDirection: "column", padding: 24 }} data-testid="join-intake">
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {stepDots}
        {canSkip ? (
          <button data-testid="skip-btn" onClick={() => (step === TOTAL ? submitPrefs(false) : setStep(step + 1))}
                  disabled={busy}
                  style={{ background: "transparent", color: META, border: "none",
                           fontFamily: "Arial", fontSize: 14, cursor: "pointer", textDecoration: "underline" }}>
            skip this one
          </button>
        ) : <span />}
      </header>

      <main style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-start",
                     maxWidth: 720, margin: "0 auto", width: "100%", paddingTop: 24, paddingBottom: 24 }}>

        {step === 1 && (
          <Screen tag={`step 1 of ${TOTAL}`} title="what should we call you?"
                  sub="this fills the profile. you can change it later.">
            {/* Photo */}
            <div className="flex items-center gap-4" data-testid="intake-photo-row" style={{ marginBottom: 20 }}>
              {photoUrl ? (
                <img src={absUrl(photoUrl)} alt="" data-testid="intake-photo-preview"
                     style={{ width: 76, height: 76, borderRadius: 999, objectFit: "cover",
                              border: `2px solid ${LINE}` }} />
              ) : (
                <div data-testid="intake-photo-placeholder"
                     style={{ width: 76, height: 76, borderRadius: 999, background: YELLOW,
                              color: "#1A1A30", display: "grid", placeItems: "center",
                              fontFamily: "Poppins", fontWeight: 800, fontSize: 28 }}>
                  {initial}
                </div>
              )}
              <button type="button" onClick={onPickPhoto} disabled={uploading}
                      data-testid="intake-photo-pick"
                      style={{ padding: "10px 16px", borderRadius: 999,
                               background: CARD, border: `1px solid ${YELLOW}`, color: "var(--ww-text)",
                               fontFamily: "Poppins", fontWeight: 600, fontSize: 13,
                               textTransform: "lowercase", cursor: uploading ? "wait" : "pointer",
                               display: "inline-flex", alignItems: "center", gap: 6, minHeight: 44 }}>
                <Camera size={16} /> {uploading ? "uploading…" : (photoUrl ? "change photo" : "add photo · optional")}
              </button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
                     data-testid="intake-photo-input" onChange={onPhotoChange} style={{ display: "none" }} />
            </div>

            <Label>your name</Label>
            <TextInput data-testid="intake-name" value={displayName}
                       onChange={(e) => setDisplayName(e.target.value)}
                       placeholder="e.g. vania n." maxLength={120} error={!!nameError} />
            {nameError && <ErrorLine>{nameError}</ErrorLine>}

            <Label style={{ marginTop: 16 }}>@handle</Label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
                             fontFamily: "Arial", color: META, fontSize: 15 }}>@</span>
              <TextInput data-testid="intake-handle" value={effectiveHandle}
                         onChange={(e) => { setHandleTouched(true); setHandle(e.target.value.replace(/^@+/, "")); }}
                         placeholder={suggestedHandle || "yourhandle"} maxLength={30}
                         style={{ paddingLeft: 28 }} error={!!handleError} />
            </div>
            <Hint>letters, numbers, and _ · 2-30 chars · must be unique</Hint>
            {handleError && <ErrorLine>{handleError}</ErrorLine>}

            <Label style={{ marginTop: 16 }}>one line about you · optional</Label>
            <TextInput data-testid="intake-oneliner" value={oneLiner}
                       onChange={(e) => setOneLiner(e.target.value.slice(0, 80))}
                       placeholder="e.g. loves live music and long brunches."
                       maxLength={80} />
            <Hint>{oneLiner.length}/80 characters</Hint>

            {/* Wave 12.18 (Vania 1 Oct) · required 18+ confirmation. Hidden
                for existing members who already ticked. No DOB, no age. */}
            {!ageAlreadySet && (
              <label htmlFor="age-confirm-cb" data-testid="age-confirm-row"
                     style={{ display: "flex", alignItems: "flex-start", gap: 10,
                              marginTop: 20, cursor: "pointer",
                              fontFamily: "Arial", fontSize: 14, color: "var(--ww-text)" }}>
                <input id="age-confirm-cb" type="checkbox"
                       data-testid="age-confirm-checkbox"
                       checked={ageConfirmed}
                       onChange={(e) => setAgeConfirmed(e.target.checked)}
                       style={{ marginTop: 3, width: 18, height: 18, accentColor: YELLOW }} />
                <span>i'm 18 or older <span style={{ color: META }}>(required)</span></span>
              </label>
            )}

            <p className="mt-4" data-testid="intake-legal-line"
               style={{ fontFamily: "Arial", fontSize: 12, color: META }}>
              by continuing you agree to our{" "}
              <a href="/terms" data-testid="intake-terms-link"
                 style={{ color: "var(--ww-text)", textDecoration: "underline" }}>terms</a>
              {" "}and{" "}
              <a href="/privacy" data-testid="intake-privacy-link"
                 style={{ color: "var(--ww-text)", textDecoration: "underline" }}>privacy policy</a>.
            </p>
          </Screen>
        )}

        {step === 2 && (
          <Screen tag={`step 2 of ${TOTAL}`} title="your perfect moments"
                  sub="pick as many as you like.">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
              {MOMENTS.map((m) => (
                <MomentCard key={m.key} moment={m}
                            on={moments.includes(m.key)}
                            onClick={() => toggle(moments, setMoments, m.key)} />
              ))}
            </div>
          </Screen>
        )}

        {step === 3 && (
          <Screen tag={`step 3 of ${TOTAL}`} title="the best times out leave you…"
                  sub="pick any.">
            <TileGrid cols={2}>
              {FEELINGS.map((f) => (
                <Tile key={f.key} on={feelings.includes(f.key)}
                      onClick={() => toggle(feelings, setFeelings, f.key)}
                      testId={`feeling-${f.key}`} big>
                  {f.label}
                </Tile>
              ))}
            </TileGrid>
          </Screen>
        )}

        {step === 4 && (
          <Screen tag={`step 4 of ${TOTAL}`} title="who's usually with you?"
                  sub="pick any.">
            <TileGrid cols={2}>
              {COMPANY.map((c) => (
                <Tile key={c.key} on={company.includes(c.key)}
                      onClick={() => toggle(company, setCompany, c.key)}
                      testId={`company-${c.key}`} big>
                  {c.label}
                </Tile>
              ))}
            </TileGrid>
          </Screen>
        )}

        {step === 5 && (
          <Screen tag={`step 5 of ${TOTAL}`} title="where do you usually head?"
                  sub="pick your sides, or anywhere. tap a side again to narrow down.">
            <AreaPickerTwoLevel
              areas={areasHome} setAreas={setAreasHome}
              testPrefix="area-home"
              includeAnywhere />
            <div style={{ height: 1, background: LINE, margin: "16px 0 4px" }} />
            <p style={{ fontFamily: "Poppins", fontWeight: 700, textTransform: "lowercase",
                         fontSize: 18, letterSpacing: "-0.02em", color: "var(--ww-text)", marginTop: 4 }}>
              anywhere you'd love to explore?
            </p>
            <p style={{ fontFamily: "Arial", fontSize: 13, color: META, marginTop: 2, marginBottom: 8 }}>
              optional. we'll mix events from here into your feed, tagged "somewhere new".
            </p>
            <AreaPickerTwoLevel
              areas={areasExplore} setAreas={setAreasExplore}
              testPrefix="area-explore" />
          </Screen>
        )}

        {step === 6 && (
          <Screen tag={`step 6 of ${TOTAL}`} title="a good outing is worth…"
                  sub="pick one.">
            <div className="flex flex-col gap-3">
              {BUDGETS.map((b) => (
                <Tile key={b.key} on={budget === b.key} onClick={() => setBudget(b.key)}
                      testId={`budget-${b.key}`} big>{b.label}</Tile>
              ))}
            </div>
          </Screen>
        )}

        {step === 7 && (
          <Screen tag={`step 7 of ${TOTAL}`} title="tell us about a day or night you still talk about"
                  sub="optional. we keep this private — it just helps us learn what you love.">
            <textarea data-testid="intake-memory" value={memory}
                      onChange={(e) => setMemory(e.target.value.slice(0, 200))}
                      placeholder="anything comes to mind…"
                      rows={4}
                      style={{ width: "100%", padding: "14px", borderRadius: 12,
                               background: "var(--ww-sunken)", border: `1px solid ${LINE}`, color: "var(--ww-text)",
                               fontFamily: "Arial", fontSize: 15, outline: "none", resize: "vertical",
                               minHeight: 96 }} />
            <p style={{ fontFamily: "Arial", fontSize: 12, color: META, marginTop: 6 }}>
              {memory.length}/200 characters
            </p>
          </Screen>
        )}

        {/* Wave 10D (Vania 1 Oct) · gender-only step. No age / DOB anywhere.
            Single-tap. "prefer not to say" is a first-class answer. */}
        {step === 8 && (
          <Screen tag={`step 8 of ${TOTAL}`} title="one last thing"
                  sub="helps us bring the right events to the right people. never shown on your profile.">
            <TileGrid cols={2}>
              {GENDERS.map((g) => (
                <Tile key={g.key} on={gender === g.key}
                      onClick={() => setGender(gender === g.key ? "" : g.key)}
                      testId={`gender-${g.key}`} big>
                  {g.label}
                </Tile>
              ))}
            </TileGrid>
          </Screen>
        )}
      </main>

      <footer style={{ display: "flex", gap: 12, maxWidth: 720, margin: "0 auto", width: "100%" }}>
        <button data-testid="back-btn" onClick={() => setStep(Math.max(1, step - 1))}
                disabled={step === 1 || busy}
                style={{ flex: 1, padding: "16px", borderRadius: 999,
                         background: "transparent", color: step === 1 ? "var(--ww-text-muted)" : "var(--ww-text)",
                         border: `1px solid ${LINE}`, fontFamily: "Poppins", fontWeight: 600, fontSize: 15,
                         textTransform: "lowercase", cursor: step === 1 ? "default" : "pointer" }}>back</button>
        <button data-testid="next-btn" onClick={onNext}
                disabled={busy}
                style={{ flex: 2, padding: "16px", borderRadius: 999,
                         background: YELLOW, color: "#1A1A30", border: `1px solid ${YELLOW}`,
                         fontFamily: "Poppins", fontWeight: 700, fontSize: 15,
                         textTransform: "lowercase", cursor: "pointer" }}>
          {step < TOTAL ? "next" : (busy ? "saving…" : "done, take me in")}
        </button>
      </footer>
    </div>
  );
}

/* ---------- helpers ---------- */

function Screen({ tag, title, sub, children }) {
  // 12.19a (Vania 1 Oct) — eyebrow was Kili Yellow on cream (1.3:1, fails 4.5).
  // Deep ink via --ww-text passes on both grounds: cream → #1A1A30, night → #FBF4E9.
  return (
    <>
      <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em",
                  fontSize: 11, color: "var(--ww-text)", fontWeight: 700 }}>{tag}</p>
      <h1 className="mt-2 mb-2 lowercase"
          style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 30, letterSpacing: "-0.025em",
                   lineHeight: 1.05 }}>
        {title}
      </h1>
      {sub && <p style={{ fontFamily: "Arial", fontSize: 14, color: META, marginBottom: 20 }}>{sub}</p>}
      {children}
    </>
  );
}
function TileGrid({ children, cols = 2 }) {
  return <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>{children}</div>;
}
function Tile({ on, onClick, children, testId, big }) {
  return (
    <button data-testid={testId} onClick={onClick}
            style={{
              padding: big ? "18px 16px" : "16px 14px", borderRadius: 14,
              background: on ? YELLOW : "transparent",
              color: on ? "#1A1A30" : "var(--ww-text)",
              border: `2px solid ${on ? YELLOW : LINE}`,
              fontFamily: "Poppins", fontWeight: 700, fontSize: big ? 16 : 15,
              textAlign: "left", cursor: "pointer",
              minHeight: big ? 56 : 52, textTransform: "lowercase",
            }}>{children}</button>
  );
}
function MomentCard({ moment, on, onClick }) {
  const tone = IKO[moment.iko] || {};
  return (
    <button data-testid={`moment-${moment.key}`} onClick={onClick}
            style={{
              display: "flex", flexDirection: "column", overflow: "hidden",
              background: on ? YELLOW : CARD,
              border: `2px solid ${on ? YELLOW : LINE}`, borderRadius: 14,
              cursor: "pointer", padding: 0, minHeight: 148,
              transition: "transform 120ms ease",
              transform: on ? "scale(0.985)" : "scale(1)",
            }}>
      {/* Msg 232 · consistent flat illustration per moment, iko-tinted. */}
      <div style={{ height: 84, position: "relative", overflow: "hidden" }}>
        <MomentIllustration momentKey={moment.key} iko={moment.iko} on={on} />
        <span style={{ position: "absolute", left: 10, bottom: 6,
                       fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em",
                       fontSize: 10, color: on ? "#1A1A30" : (tone.bright || "#FFFFFF") }}>
          {tone.label ? tone.label.toLowerCase() : moment.iko}
        </span>
      </div>
      <div style={{ padding: "10px 12px 12px",
                     fontFamily: "Poppins", fontWeight: 700, fontSize: 13,
                     textTransform: "lowercase", textAlign: "left",
                     color: on ? "#1A1A30" : "var(--ww-text)", lineHeight: 1.2 }}>
        {moment.label}
      </div>
    </button>
  );
}
function PillButton({ on, onClick, children, testId, style }) {
  return (
    <button data-testid={testId} onClick={onClick}
            style={{
              padding: "10px 14px", borderRadius: 999,
              background: on ? CARD : "transparent",
              color: on ? YELLOW : "var(--ww-text)",
              border: `2px solid ${on ? YELLOW : LINE}`,
              fontFamily: "Arial", fontSize: 13, cursor: "pointer",
              minHeight: 40, ...style,
            }}>{children}</button>
  );
}

/**
 * Msg 232 · Two-level side/neighbourhood picker.
 * Tap a side card to add it (its side name is stored in `areas`). Tap again
 * to expand the neighbourhoods. Picking neighbourhoods keeps the side too so
 * a bare-side pick counts as the whole side.
 */
function AreaPickerTwoLevel({ areas, setAreas, testPrefix, includeAnywhere = false }) {
  const [expanded, setExpanded] = React.useState({});
  const toggleSide = (name) => {
    // If already on, tapping toggles expand instead of unselecting.
    if (areas.includes(name)) {
      setExpanded((e) => ({ ...e, [name]: !e[name] }));
    } else {
      setAreas([...areas, name]);
      setExpanded((e) => ({ ...e, [name]: true }));
    }
  };
  const removeSide = (name, list) => {
    setAreas(areas.filter((a) => a !== name && !list.includes(a)));
    setExpanded((e) => ({ ...e, [name]: false }));
  };
  const toggleHood = (name) => {
    setAreas(areas.includes(name) ? areas.filter((x) => x !== name) : [...areas, name]);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: "58vh", overflowY: "auto" }}>
      {AREA_GROUPS.map(([sideName, list]) => {
        const on = areas.includes(sideName);
        const expandedNow = !!expanded[sideName];
        const hoodsPicked = list.filter((h) => areas.includes(h)).length;
        return (
          <div key={sideName} data-testid={`${testPrefix}-side-block-${sideName.toLowerCase().replace(/[^a-z]+/g, "-")}`}
               style={{ background: on ? "var(--ww-sunken)" : "transparent",
                        border: `1.5px solid ${on ? YELLOW : LINE}`,
                        borderRadius: 14, padding: 10 }}>
            <button data-testid={`${testPrefix}-side-${sideName.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => toggleSide(sideName)}
                    style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                             background: "transparent", border: 0, cursor: "pointer",
                             fontFamily: "Poppins", fontWeight: 700, fontSize: 15,
                             color: on ? YELLOW : "var(--ww-text)", textTransform: "lowercase", minHeight: 44 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                {sideName.toLowerCase()}
                {on && hoodsPicked > 0 && (
                  <span style={{ fontFamily: "Arial", fontSize: 11, color: META }}>
                    · {hoodsPicked} area{hoodsPicked > 1 ? "s" : ""}
                  </span>
                )}
              </span>
              <span style={{ fontFamily: "Arial", fontSize: 12, color: META }}>
                {on ? (expandedNow ? "hide" : "narrow down ›") : "tap to add"}
              </span>
            </button>
            {on && expandedNow && (
              <div className="flex flex-wrap gap-2" style={{ marginTop: 8 }}>
                {list.map((h) => (
                  <PillButton key={h} on={areas.includes(h)}
                              onClick={() => toggleHood(h)}
                              testId={`${testPrefix}-${h.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {h}
                  </PillButton>
                ))}
                <button data-testid={`${testPrefix}-clear-${sideName.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        onClick={() => removeSide(sideName, list)}
                        style={{ background: "transparent", border: 0, color: META, fontFamily: "Arial", fontSize: 12,
                                 textDecoration: "underline", cursor: "pointer", padding: "10px 6px", minHeight: 40 }}>
                  clear this side
                </button>
              </div>
            )}
          </div>
        );
      })}
      {includeAnywhere && (
        <PillButton on={areas.includes("anywhere")}
                    onClick={() => setAreas(areas.includes("anywhere") ? areas.filter((x) => x !== "anywhere") : [...areas, "anywhere"])}
                    testId={`${testPrefix}-anywhere`}
                    style={{ alignSelf: "flex-start", marginTop: 4 }}>
          anywhere in Nairobi
        </PillButton>
      )}
    </div>
  );
}
function Label({ children, style }) {
  return <p style={{ fontFamily: "Poppins", fontWeight: 600, textTransform: "lowercase",
                     fontSize: 13, color: "var(--ww-text)", marginBottom: 6, ...style }}>{children}</p>;
}
function Hint({ children }) {
  return <p style={{ fontFamily: "Arial", fontSize: 12, color: META, marginTop: 4 }}>{children}</p>;
}
function ErrorLine({ children }) {
  return <p style={{ fontFamily: "Arial", fontSize: 12, color: "#FF6B9D", marginTop: 4 }}>{children}</p>;
}
function TextInput({ style, error, ...rest }) {
  return <input {...rest}
                style={{ width: "100%", padding: "12px 14px", borderRadius: 12,
                         background: "var(--ww-sunken)", border: `1px solid ${error ? "#FF6B9D" : LINE}`,
                         color: "var(--ww-text)", fontFamily: "Arial", fontSize: 15, outline: "none",
                         minHeight: 44, ...style }} />;
}

function titleForArea(a) {
  const all = ["Westlands", "Parklands", "Spring Valley", "Loresho", "Riverside", "Lavington",
               "Kilimani", "Kileleshwa", "Hurlingham", "Upper Hill", "Ngong Road",
               "Karen", "Lang'ata", "Rongai", "Ngong",
               "Gigiri", "Runda", "Muthaiga", "Rosslyn", "Ruaka", "Kiambu Road",
               "Kasarani", "Roysambu", "Garden City", "Kahawa", "Ruiru", "Juja",
               "CBD", "Ngara", "South B", "South C", "Industrial Area", "Syokimau", "Kitengela",
               "Eastleigh", "Buruburu", "Donholm", "Umoja", "Embakasi", "anywhere"];
  const found = all.find((x) => x.toLowerCase() === a.toLowerCase());
  return found || a;
}
